/**
 * Purpose:
 * Client-side layout wrapper for protected pages.
 * Manages the Header, Menu sidebar, and main content area.
 *
 * Notes:
 * - Composition-only component
 * - Delegates all logic to hooks
 * - Integrates logout functionality via useLogout hook
 * - i18n namespace common + account for header avatar actions
 * - Provides responsive layout structure (sidebar + content)
 * - Smooth animations synchronized with menu transitions (350ms)
 */

'use client';

import React, { Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/layout/Header/Header';
import { Menu } from '@/components/layout/Menu';
import { PageStateProvider } from '@/components/layout/pageState';
import { useLogout } from '@/features/auth/shared/hooks/useLogout';
import { ForbiddenAccessToast } from '@/features/auth/shared/components/ForbiddenAccessToast';
import { useSessionAccount } from '@/lib/auth/useSessionAccount';
import { useProtectedLayoutState } from './(hooks)/useProtectedLayoutState';
import { useProtectedLayoutResize } from './(hooks)/useProtectedLayoutResize';
import { useProtectedLayoutClasses } from './(hooks)/useProtectedLayoutClasses';
import { protectedLayoutStyles } from './ProtectedLayoutClient.styles';

interface IProtectedLayoutClientProps {
  children: React.ReactNode;
}

export function ProtectedLayoutClient({
  children,
}: IProtectedLayoutClientProps) {
  const router = useRouter();
  const { t } = useTranslation('common');
  const { t: tAccount } = useTranslation('account');
  const {
    isMenuOpen,
    isMenuCollapsed,
    isMounted,
    handleMenuToggle,
    handleMenuClose,
    handleCollapsedToggle,
    setMounted,
  } = useProtectedLayoutState();

  useProtectedLayoutResize(setMounted, handleMenuClose);

  const { getMainClasses } = useProtectedLayoutClasses(
    isMenuCollapsed,
    isMounted
  );

  const { logout, isLoading: isLoggingOut } = useLogout();
  const { user_name: userName } = useSessionAccount();

  return (
    <div className={protectedLayoutStyles.mainContainer}>
      <Suspense fallback={null}>
        <ForbiddenAccessToast />
      </Suspense>

      {/* Fixed Header */}
      <Header
        userName={userName ?? undefined}
        isMenuOpen={isMenuOpen}
        onMenuToggle={handleMenuToggle}
        onProfile={() => router.push('/profile')}
        onLogout={logout}
        isLoggingOut={isLoggingOut}
        userMenuAriaLabel={t('menu.user_actions')}
        profileLabel={tAccount('page.profile')}
        logoutLabel={t('menu.logout')}
        loggingOutLabel={t('menu.logging_out')}
      />

      {/* Sidebar Menu */}
      <Menu
        isOpen={isMenuOpen}
        isCollapsed={isMenuCollapsed}
        onClose={handleMenuClose}
        onToggleCollapsed={handleCollapsedToggle}
      />

      {/* Main Content Area */}
      <main className={getMainClasses()}>
        <div className={protectedLayoutStyles.contentContainer}>
          <PageStateProvider>{children}</PageStateProvider>
        </div>
      </main>
    </div>
  );
}
